/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as utils from '@/utils';
import './style.less';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function createToggle(name, valueOff, valueOn, opts = {}) {
    return tsx.componentFactoryOf().create({
        name: `toggle-${name}`,
        props: {
            id: p(String).optional,
            value: p.ofType().required,
            labelOff: p(String).default(opts.labelOff ?? 'No'),
            labelOn: p(String).default(opts.labelOn ?? 'Yes'),
            disabled: p(Boolean).default(false),
            disabledText: p(String).default(''),
            noColors: p(Boolean).default(false),
            inline: p(Boolean).default(false),
            tabindex: p(Number).optional,
        },
        /** @returns The initial runtime data. */
        data() {
            const uniqueId = utils.getUniqueId();
            return { uniqueId };
        },
        computed: {
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            checked() {
                return this.value.equals(Maybe.of(valueOn));
            },
        },
        methods: {
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            toggle(maybeValue) {
                // Use the given value if it's a Just, otherwise toggle the
                // current value if it's set.
                if (!this.disabled) {
                    maybeValue
                        .altLazy(() => this.value.map(val => (val === valueOn ? valueOff : valueOn)))
                        .ifJust(value => tsx.emitOn(this, 'onInput', { value }));
                }
            },
        },
        /** @returns The rendered component. */
        render() {
            const id = this.id ?? `toggle-${this.uniqueId}`;
            const onId = `${id}-on`;
            const offId = `${id}-off`;
            const radioName = `toggleValue${this.uniqueId}`;
            return (<div id={id} class={{
                    'toggle-container': true,
                    checked: this.checked,
                    disabled: this.disabled,
                    colors: !this.noColors,
                    'd-inline': this.inline,
                    'has-no-value': this.value.isNothing(),
                }} tabindex={this.disabled ? -1 : 0} role="radiogroup">
                    <input type="radio" id={offId} name={radioName} onClick={() => this.toggle(Maybe.of(valueOff))} checked={!this.checked} tabindex={0} disabled={this.disabled}/>
                    <label for={offId} class="label label-off p-0">
                        {utils.renderScopedSlot({ value: valueOff }, this.$scopedSlots.labelOff, () => this.labelOff)}
                    </label>
                    <div onClick={() => this.toggle(Maybe.empty())} class="toggle"/>
                    <input type="radio" id={onId} name={radioName} onClick={() => this.toggle(Maybe.of(valueOn))} checked={this.checked} tabindex={0} disabled={this.disabled}/>
                    <label for={onId} class="label label-on p-0">
                        {utils.renderScopedSlot({ value: valueOn }, this.$scopedSlots.labelOn, () => this.labelOn)}
                    </label>

                    {utils.ifOrEmpty(this.disabled && this.disabledText !== '', () => (<b-popover placement="top" triggers="hover" target={id}>
                            {this.disabledText}
                        </b-popover>))}
                </div>);
        },
    });
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const BooleanToggle = createToggle('boolean-toggle', false, true);
